/**
 * A small, curated, static city list for the sunrise/sunset location picker
 * (`src/ui/shared/settings/location.ts`, Settings screen). Same category as
 * `src/domain/content/quotes.ts`'s "a small static content module, not a
 * live service" precedent — this repo's standing Non-Goal rules out a real
 * geocoding API call (city name -> lat/long) or IP-geolocation lookup, so
 * this bundles real, fixed lat/long pairs for a genuinely useful set of
 * major world cities instead.
 *
 * Coordinates are each city's commonly-cited downtown/city-center point —
 * accurate enough for `computeSunTimes`'s own stated "a few minutes"
 * tolerance (it is an ambient-chrome approximation, not a navigation-grade
 * ephemeris), not survey-grade precision.
 *
 * Each city also carries its IANA timezone name (e.g. `'Europe/London'`) so
 * `computeSunTimes` can resolve the CITY's own real UTC offset (via the
 * browser's built-in `Intl` timezone database — no network call, no bundled
 * tz data) instead of assuming the offset of whatever timezone the device
 * itself happens to be running in. That assumption silently produced
 * correct-looking results only when the picked city shared the device's own
 * timezone (true for every city exercised before this list existed); once a
 * user picks a city in a different zone, the browser's own offset is simply
 * the wrong number — the bug this field exists to fix.
 *
 * List is intentionally NOT exhaustive (CLAUDE.md's own framing: "doesn't
 * need to be exhaustive, just genuinely useful") — a broad mix across North
 * America, Europe, Asia, and Australia/Oceania. Sorted alphabetically by
 * name for a predictable, scannable picker order.
 */

export interface CityLocation {
  readonly name: string;
  readonly lat: number;
  readonly long: number;
  readonly timezone: string;
}

export const CITIES: readonly CityLocation[] = [
  { name: 'Amsterdam, Netherlands', lat: 52.3676, long: 4.9041, timezone: 'Europe/Amsterdam' },
  { name: 'Atlanta, USA', lat: 33.749, long: -84.388, timezone: 'America/New_York' },
  { name: 'Auckland, New Zealand', lat: -36.8485, long: 174.7633, timezone: 'Pacific/Auckland' },
  { name: 'Austin, USA', lat: 30.2672, long: -97.7431, timezone: 'America/Chicago' },
  { name: 'Bangkok, Thailand', lat: 13.7563, long: 100.5018, timezone: 'Asia/Bangkok' },
  { name: 'Barcelona, Spain', lat: 41.3851, long: 2.1734, timezone: 'Europe/Madrid' },
  { name: 'Beijing, China', lat: 39.9042, long: 116.4074, timezone: 'Asia/Shanghai' },
  { name: 'Berlin, Germany', lat: 52.52, long: 13.405, timezone: 'Europe/Berlin' },
  { name: 'Boston, USA', lat: 42.3601, long: -71.0589, timezone: 'America/New_York' },
  { name: 'Brisbane, Australia', lat: -27.4698, long: 153.0251, timezone: 'Australia/Brisbane' },
  { name: 'Brussels, Belgium', lat: 50.8503, long: 4.3517, timezone: 'Europe/Brussels' },
  { name: 'Calgary, Canada', lat: 51.0447, long: -114.0719, timezone: 'America/Edmonton' },
  { name: 'Chicago, USA', lat: 41.8781, long: -87.6298, timezone: 'America/Chicago' },
  { name: 'Copenhagen, Denmark', lat: 55.6761, long: 12.5683, timezone: 'Europe/Copenhagen' },
  { name: 'Dallas, USA', lat: 32.7767, long: -96.797, timezone: 'America/Chicago' },
  { name: 'Delhi, India', lat: 28.7041, long: 77.1025, timezone: 'Asia/Kolkata' },
  { name: 'Denver, USA', lat: 39.7392, long: -104.9903, timezone: 'America/Denver' },
  { name: 'Dubai, UAE', lat: 25.2048, long: 55.2708, timezone: 'Asia/Dubai' },
  { name: 'Dublin, Ireland', lat: 53.3498, long: -6.2603, timezone: 'Europe/Dublin' },
  { name: 'Hong Kong', lat: 22.3193, long: 114.1694, timezone: 'Asia/Hong_Kong' },
  { name: 'Honolulu, USA', lat: 21.3069, long: -157.8583, timezone: 'Pacific/Honolulu' },
  { name: 'Houston, USA', lat: 29.7604, long: -95.3698, timezone: 'America/Chicago' },
  { name: 'Istanbul, Turkey', lat: 41.0082, long: 28.9784, timezone: 'Europe/Istanbul' },
  { name: 'Jakarta, Indonesia', lat: -6.2088, long: 106.8456, timezone: 'Asia/Jakarta' },
  { name: 'London, United Kingdom', lat: 51.5072, long: -0.1276, timezone: 'Europe/London' },
  { name: 'Los Angeles, USA', lat: 34.0522, long: -118.2437, timezone: 'America/Los_Angeles' },
  { name: 'Madrid, Spain', lat: 40.4168, long: -3.7038, timezone: 'Europe/Madrid' },
  { name: 'Melbourne, Australia', lat: -37.8136, long: 144.9631, timezone: 'Australia/Melbourne' },
  { name: 'Mexico City, Mexico', lat: 19.4326, long: -99.1332, timezone: 'America/Mexico_City' },
  { name: 'Miami, USA', lat: 25.7617, long: -80.1918, timezone: 'America/New_York' },
  { name: 'Milan, Italy', lat: 45.4642, long: 9.19, timezone: 'Europe/Rome' },
  { name: 'Montreal, Canada', lat: 45.5019, long: -73.5674, timezone: 'America/Toronto' },
  { name: 'Mumbai, India', lat: 19.076, long: 72.8777, timezone: 'Asia/Kolkata' },
  { name: 'New York, USA', lat: 40.7128, long: -74.006, timezone: 'America/New_York' },
  { name: 'Osaka, Japan', lat: 34.6937, long: 135.5023, timezone: 'Asia/Tokyo' },
  { name: 'Oslo, Norway', lat: 59.9139, long: 10.7522, timezone: 'Europe/Oslo' },
  { name: 'Paris, France', lat: 48.8566, long: 2.3522, timezone: 'Europe/Paris' },
  { name: 'Perth, Australia', lat: -31.9505, long: 115.8605, timezone: 'Australia/Perth' },
  { name: 'Philadelphia, USA', lat: 39.9526, long: -75.1652, timezone: 'America/New_York' },
  { name: 'Phoenix, USA', lat: 33.4484, long: -112.074, timezone: 'America/Phoenix' },
  { name: 'Portland, USA', lat: 45.5152, long: -122.6784, timezone: 'America/Los_Angeles' },
  { name: 'Prague, Czechia', lat: 50.0755, long: 14.4378, timezone: 'Europe/Prague' },
  { name: 'Rome, Italy', lat: 41.9028, long: 12.4964, timezone: 'Europe/Rome' },
  { name: 'San Francisco, USA', lat: 37.7749, long: -122.4194, timezone: 'America/Los_Angeles' },
  { name: 'Sao Paulo, Brazil', lat: -23.5505, long: -46.6333, timezone: 'America/Sao_Paulo' },
  { name: 'Seattle, USA', lat: 47.6062, long: -122.3321, timezone: 'America/Los_Angeles' },
  { name: 'Seoul, South Korea', lat: 37.5665, long: 126.978, timezone: 'Asia/Seoul' },
  { name: 'Shanghai, China', lat: 31.2304, long: 121.4737, timezone: 'Asia/Shanghai' },
  { name: 'Singapore', lat: 1.3521, long: 103.8198, timezone: 'Asia/Singapore' },
  { name: 'Stockholm, Sweden', lat: 59.3293, long: 18.0686, timezone: 'Europe/Stockholm' },
  { name: 'Sydney, Australia', lat: -33.8688, long: 151.2093, timezone: 'Australia/Sydney' },
  { name: 'Taipei, Taiwan', lat: 25.033, long: 121.5654, timezone: 'Asia/Taipei' },
  { name: 'Tokyo, Japan', lat: 35.6762, long: 139.6503, timezone: 'Asia/Tokyo' },
  { name: 'Toronto, Canada', lat: 43.6532, long: -79.3832, timezone: 'America/Toronto' },
  { name: 'Vancouver, Canada', lat: 49.2827, long: -123.1207, timezone: 'America/Vancouver' },
  { name: 'Vienna, Austria', lat: 48.2082, long: 16.3738, timezone: 'Europe/Vienna' },
  { name: 'Warsaw, Poland', lat: 52.2297, long: 21.0122, timezone: 'Europe/Warsaw' },
  { name: 'Washington DC, USA', lat: 38.9072, long: -77.0369, timezone: 'America/New_York' },
  { name: 'Zurich, Switzerland', lat: 47.3769, long: 8.5417, timezone: 'Europe/Zurich' },
];
